import { useRef, useState } from "react";
import { Film, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, imageUrl } from "@/lib/api";

const MAX = 250 * 1024 * 1024;
const TYPES = ["video/mp4", "video/quicktime", "video/webm"];

async function uploadVideo(file, onProgress) {
  const { data } = await api.post("/uploads/video/init", { size: file.size, content_type: file.type });
  let offset = 0;
  while (offset < file.size) {
    const chunk = file.slice(offset, offset + data.chunk_size);
    let tries = 0;
    for (;;) {
      try {
        const res = await api.put(`/uploads/video/${data.upload_id}/chunk?offset=${offset}`, chunk, { headers: { "Content-Type": "application/octet-stream" } });
        offset = res.data.received;
        break;
      } catch (e) { if (++tries >= 3) throw e; }
    }
    onProgress(Math.round((offset / file.size) * 100));
  }
  const done = await api.post(`/uploads/video/${data.upload_id}/complete`, null, { timeout: 15 * 60 * 1000 });
  return done.data.path;
}

export const VideoUploader = ({ value, onChange, onBusy, testId = "exp-video", label = "Video", addLabel = "Add a video" }) => {
  const input = useRef(null);
  const [progress, setProgressState] = useState(null);
  const setProgress = (v) => { setProgressState(v); onBusy?.(v !== null); };
  const pick = async (file) => {
    if (input.current) input.current.value = "";
    if (!file) return;
    if (!TYPES.includes(file.type)) return toast.error("Please choose an MP4, MOV or WEBM video");
    if (file.size > MAX) return toast.error("Videos must be 250MB or smaller");
    setProgress(0);
    try { onChange(await uploadVideo(file, setProgress)); toast.success("Video uploaded"); }
    catch (e) { toast.error(apiError(e)); }
    finally { setProgress(null); }
  };
  return (
    <div data-testid={testId}>
      <p className="field-label">{label} <span className="font-normal text-muted-foreground">(optional, 1 video, MP4/MOV/WEBM up to 250MB)</span></p>
      {value ? (
        <div className="relative max-w-md overflow-hidden rounded-xl border border-border bg-black">
          <video src={imageUrl(value)} controls preload="metadata" className="aspect-video w-full" data-testid={`${testId}-preview`} />
          <button type="button" aria-label="Remove video" onClick={() => onChange(null)} className="absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-full bg-background/85 hover:bg-background" data-testid={`${testId}-remove`}><X className="h-4 w-4" /></button>
        </div>
      ) : progress !== null ? (
        <div className="max-w-md rounded-xl border border-border p-4" data-testid={`${testId}-progress`}>
          <div className="mb-2 flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Uploading video… {progress}%</div>
          <div className="h-2 overflow-hidden rounded-full bg-secondary"><div className="h-full bg-primary transition-[width] duration-300" style={{ width: `${progress}%` }} /></div>
        </div>
      ) : (
        <button type="button" onClick={() => input.current?.click()} className="flex h-28 w-full max-w-md flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border text-sm text-muted-foreground transition-colors hover:border-primary hover:text-foreground" data-testid={`${testId}-add`}>
          <Film className="h-5 w-5" /> {addLabel}
        </button>
      )}
      <input ref={input} type="file" accept={TYPES.join(",")} hidden onChange={(e) => pick(e.target.files?.[0])} data-testid={`${testId}-input`} />
    </div>
  );
};
